import {
  type InUse,
  MEDIATHEQUE_UPLOAD_OPEN_TESTID,
} from '@/components/admin/mediatheque/contracts';
import { MediathequeGrid } from '@/components/admin/mediatheque/mediatheque-grid';
import { MediathequeToolbar } from '@/components/admin/mediatheque/mediatheque-toolbar';
import { MediathequeUploadDialog } from '@/components/admin/mediatheque/mediatheque-upload-dialog';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, deleteAmbianceAsset, fetchAmbianceAssets, uploadAmbianceAsset } from '@/lib/api';
import { ambianceUploadRefusal } from '@/lib/mediatheque-labels';
import type { AmbianceAsset, AmbianceAssetKind } from '@matter_extractor/shared';
import {
  AMBIANCE_KIND_OF_FORMAT,
  ambianceAssetInUseSchema,
  ambianceFormatOf,
  filterAmbianceAssets,
} from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Médiathèque (refonte modulaire §16, H7; issue #121): the missing door onto
 * the file library the coupe kept whole - upload, list, delete - behind no
 * screen at all outside a block that already cites a file.
 *
 * ## Not the versioned layer, and not the picker
 *
 * A file is not one of the six versioned objects (§4): it carries no draft,
 * no development/production pointer and no history to open, so this screen
 * is a plain grid rather than `components/admin/versioned/`. It is also not
 * `media-picker.tsx` reused for a second purpose - that component is a "pick
 * one and close" modal with its own confirm-by-second-gesture rule, mounted
 * from inside a block or a theme bar. This screen is the other consumer of
 * the same library: consult, upload, retire - and the two only share a
 * visual idiom (the kind chips) and the upload form, never a component.
 *
 * ## Filtering is CLIENT-SIDE, on the whole library
 *
 * `ambianceAssetQuerySchema` only narrows on `q` and `kind`, and
 * `filterAmbianceAssets` is the SAME pure function the server and the picker
 * already use - a second implementation would disagree with both on the
 * first accent. No query parameter is sent: the library is read once and
 * narrowed in the page, the same trade `fetchAmbianceAssets` documents.
 *
 * ## Delete: inline confirm, then a typed 409
 *
 * `deleteAmbianceAsset` answers 200 with the library made whole again, or a
 * 409 `ASSET_IN_USE` whose `referents` this screen reads from
 * `ApiError.details` (never `.code`, which on this CONTRACT shape carries the
 * French sentence rather than the machine code - `lib/api.ts` explains the
 * asymmetry). A refused deletion REPLACES the inline confirmation with the
 * referent list rather than closing it: the operator's next honest move is to
 * go fix the experience, not to try again.
 */

export const Route = createFileRoute('/admin/mediatheque')({
  component: MediathequePage,
});

const INTRO =
  'Les images et les sons que les pages et le thème d’une expérience peuvent citer. Un fichier cité par une version taguée ne se supprime pas : remplacez-le d’abord dans l’expérience qui le cite.';

/**
 * EXPORTED for `tests/mediatheque.test.tsx`, and for nothing else - the
 * régime `routes/admin/reglages.personas.tsx` states: `vitest.config.ts`
 * leaves `src/routes/**` out of the coverage REPORT, not out of the suite.
 */
export function MediathequePage() {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const queryKey = ['ambiance-assets', email];

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<AmbianceAssetKind | 'all'>('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [inUse, setInUse] = useState<InUse | null>(null);
  /**
   * The nature of the FILE last submitted to the upload dialog, read off its
   * name before the request goes out. `ambianceUploadRefusal` needs it to
   * quote the right ceiling on `FILE_TOO_LARGE` - `AMBIANCE_ASSET_RULES` keeps
   * one `maxLabel` per kind, and the error itself carries no kind of its own.
   */
  const [uploadKind, setUploadKind] = useState<AmbianceAssetKind | null>(null);

  const assets = useQuery({
    queryKey,
    queryFn: fetchAmbianceAssets,
    retry: false,
  });
  const rejected = useRejectOn401(assets.error);

  const upload = useMutation({
    mutationFn: ({ file, license, tags }: { file: File; license: string; tags: string }) =>
      uploadAmbianceAsset(file, license, tags),
    onSuccess: (created) => {
      // The response is the ONE asset created, not the library whole (unlike
      // delete's `AmbianceAssetList`) - prepend it rather than invalidate, the
      // régime `deleteAmbianceAsset`'s `setQueryData` below already follows.
      // Most-recently-uploaded-first (§16) still holds: `sorted` below re-sorts
      // on `uploadedAt` at every render, so a stale insertion order here would
      // self-correct on the very next paint regardless.
      queryClient.setQueryData(queryKey, (previous: { assets: AmbianceAsset[] } | undefined) =>
        previous === undefined ? previous : { assets: [created, ...previous.assets] },
      );
      setUploadOpen(false);
    },
  });

  const removeAsset = useMutation({
    mutationFn: (asset: AmbianceAsset) => deleteAmbianceAsset(asset.id),
    onSuccess: (list) => {
      queryClient.setQueryData(queryKey, list);
      setConfirmingId(null);
      setInUse(null);
    },
    onError: (error, asset) => {
      const parsed =
        error instanceof ApiError ? ambianceAssetInUseSchema.safeParse(error.details) : null;
      if (parsed?.success) {
        setInUse({ assetId: asset.id, referents: parsed.data.referents });
      }
      // Anything else (transport failure, 500) leaves the inline confirmation
      // open with the generic sentence below it, so the gesture can be
      // retried - the régime `reglages-personas.tsx` states for a write that
      // settled NOTHING.
    },
  });
  /**
   * Derived from the mutation itself rather than tracked as a second
   * `useState` (issue #121 review): "is a delete running, and for which id"
   * then has a single source of truth, and a second delete started on another
   * card while this one is still in flight can no longer silently overwrite
   * `removeAsset`'s variables.
   */
  const deletingId = removeAsset.isPending ? removeAsset.variables.id : null;

  const cards = assets.data?.assets ?? [];
  const narrowed = filterAmbianceAssets(cards, {
    ...(query.trim() === '' ? {} : { q: query }),
    ...(kind === 'all' ? {} : { kind }),
  });
  // Most recently uploaded first - the one sort §16 asks for, with no control.
  const sorted = [...narrowed].sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));

  const filtered = query.trim() !== '' || kind !== 'all';
  const genericDeleteError = removeAsset.isError && inUse === null;

  return (
    <section className="space-y-5 pb-16">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="font-bold text-3xl text-bleu-nuit">Médiathèque</h1>
          <p className="max-w-[95ch] text-gray-600 text-sm">{INTRO}</p>
        </div>
        <Button
          type="button"
          data-testid={MEDIATHEQUE_UPLOAD_OPEN_TESTID}
          onClick={() => {
            // A stale refusal from an earlier attempt in this dialog session
            // must not still be showing the moment it reopens on a fresh file.
            upload.reset();
            setUploadKind(null);
            setUploadOpen(true);
          }}
        >
          Ajouter un fichier
        </Button>
      </header>

      <MediathequeToolbar
        query={query}
        kind={kind}
        onQueryChange={setQuery}
        onKindChange={setKind}
      />

      {genericDeleteError ? (
        <Callout tone="danger" live="alert" data-testid="mediatheque-suppression-echouee">
          Le fichier n’a pas pu être supprimé. Réessayez.
        </Callout>
      ) : null}

      {assets.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement de la médiathèque" />
        </div>
      ) : null}

      {assets.isError && !rejected ? (
        <p className="text-gray-700">La médiathèque n’a pas pu être chargée.</p>
      ) : null}

      {assets.data ? (
        <MediathequeGrid
          assets={sorted}
          emptyMessage={
            filtered
              ? 'Aucun fichier ne correspond à cette recherche.'
              : 'Aucun fichier pour l’instant. « Ajouter un fichier » ouvre le téléversement.'
          }
          confirmingId={confirmingId}
          deletingId={deletingId}
          inUse={inUse}
          onRequestDelete={(asset) => {
            // A new delete target: any stale error/referents from a PREVIOUS
            // card's failed attempt must not survive onto this one.
            if (removeAsset.isPending) return;
            removeAsset.reset();
            setConfirmingId(asset.id);
            setInUse(null);
          }}
          onCancelDelete={() => setConfirmingId(null)}
          onConfirmDelete={(asset) => {
            if (removeAsset.isPending) return;
            removeAsset.mutate(asset);
          }}
          onDismissReferents={() => {
            removeAsset.reset();
            setInUse(null);
            setConfirmingId(null);
          }}
        />
      ) : null}

      {uploadOpen ? (
        <MediathequeUploadDialog
          busy={upload.isPending}
          refusal={ambianceUploadRefusal(upload.error, uploadKind)}
          onUpload={(file, license, tags) => {
            const format = ambianceFormatOf(file.name);
            setUploadKind(format === null ? null : AMBIANCE_KIND_OF_FORMAT[format]);
            upload.mutate({ file, license, tags });
          }}
          onCancel={() => {
            upload.reset();
            setUploadOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}
